import React from 'react'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { StatusChip } from 'components/StatusChip'
import { CHROME_RADIUS, TOUCH_MIN, type StatusTone } from 'core/theme.tokens'

export interface MobileRecordCardChip {
  label: string
  tone?: StatusTone
}

export interface MobileRecordCardProps {
  title: React.ReactNode
  meta?: React.ReactNode
  detail?: React.ReactNode
  trailing?: React.ReactNode
  /** Status chip pinned top-right, beside the title. */
  status?: { tone: StatusTone; label: string }
  /** Metadata chip row under the text. Shows up to `maxChips`, then `+N`. */
  chips?: MobileRecordCardChip[]
  maxChips?: number
  onClick?: () => void
  /** Navy rail by default; red for danger / selected work. */
  accent?: 'navy' | 'red' | 'none'
  selected?: boolean
}

export const MobileRecordCard: React.FC<MobileRecordCardProps> = ({
  title,
  meta,
  detail,
  trailing,
  status,
  chips,
  maxChips = 3,
  onClick,
  accent = 'navy',
  selected = false,
}) => {
  const rail =
    accent === 'none'
      ? 'transparent'
      : accent === 'red' || selected
        ? 'error.main'
        : 'primary.main'

  const shownChips = chips?.slice(0, maxChips) ?? []
  const overflowChips = chips ? chips.length - shownChips.length : 0

  const body = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        px: 2,
        py: 1.5,
        minHeight: TOUCH_MIN,
      }}
    >
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography
            variant='body2'
            sx={{ fontWeight: 700, color: 'text.primary', textWrap: 'balance', flex: 1, minWidth: 0 }}
            noWrap
          >
            {title}
          </Typography>
          {status ? (
            <StatusChip tone={status.tone} label={status.label} dense sx={{ flexShrink: 0 }} />
          ) : null}
        </Box>
        {meta ? (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }} noWrap>
            {meta}
          </Typography>
        ) : null}
        {detail ? (
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }} noWrap>
            {detail}
          </Typography>
        ) : null}
        {shownChips.length > 0 ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75 }}>
            {shownChips.map((chip, index) => (
              <StatusChip
                key={`${chip.label}-${index}`}
                tone={chip.tone ?? 'neutral'}
                label={chip.label}
                dense
                maxWidth={120}
              />
            ))}
            {overflowChips > 0 ? (
              <StatusChip tone='neutral' label={`+${overflowChips}`} dense maxWidth={false} />
            ) : null}
          </Box>
        ) : null}
      </Box>
      {trailing ? (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>{trailing}</Box>
      ) : null}
    </Box>
  )

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: `${CHROME_RADIUS}px`,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: selected ? 'error.main' : 'divider',
        boxShadow: 'none',
        '&::before': {
          content: '""',
          display: 'block',
          width: 3,
          bgcolor: rail,
          position: 'absolute',
          inset: '0 auto 0 0',
        },
        position: 'relative',
      }}
    >
      {onClick ? (
        <CardActionArea onClick={onClick} sx={{ minHeight: TOUCH_MIN }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  )
}

export const MobileRecordAction: React.FC<{
  label: string
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void
  children: React.ReactNode
  disabled?: boolean
}> = ({ label, onClick, children, disabled = false }) => (
  <IconButton
    aria-label={label}
    onClick={onClick}
    size='small'
    disabled={disabled}
    sx={{ minWidth: TOUCH_MIN, minHeight: TOUCH_MIN }}
  >
    {children}
  </IconButton>
)
